import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Logo from '@comp/Logo/Logo';
import Grow from '@base/Grow/Grow';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { useSelectorToStore } from '@hooks/reduxHooks';
import dateHelper from '@utils/dates';
import { Spacings } from '@styles/index';
import { useIsMobile } from '@stores/AppCommonData';
import { sectionsIds, getOffsetTop } from '../sections';
import HeaderSections from './HeaderSections';
import HeaderSectionsMobile from './HeaderSectionsMobile';

const StickyHeaderWrapper = styled.header`
  position: fixed;
  z-index: 20;
  left: ${Spacings.SPACING_0B};
  width: 100%;
  background-color: var(--color-base-0);
  box-shadow: ${boxShadowGenerator()};
`;

const Header = styled.div`
  width: 1136px;
  height: ${Spacings.SPACING_18B};
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_0B};
  margin: auto;
  display: flex;
  align-items: center;
`;

const StyledLogo = styled(Logo)`
  display: inline-block;
`;

const FixedHeader = ({ headerRefs, isPageReady }) => {
  const isMobileDevice = useIsMobile();
  const eventsHeaderOffsetTop = useRef(0);
  const heightScrolled = useRef(0);
  const headerRef = useRef();
  const headerHeight = useRef(0);
  const prevScrollTop = useRef(0);
  const [headerTop, setHeaderTop] = useState(-400);
  useEffect(() => {
    headerHeight.current = headerRef.current.getBoundingClientRect().height;
    eventsHeaderOffsetTop.current =
      getOffsetTop(headerRefs[sectionsIds.events].current) -
      window.innerHeight / 2;
    const onScroll = () => {
      const { scrollTop } = document.documentElement;
      const diff = scrollTop - prevScrollTop.current;
      if (
        scrollTop > eventsHeaderOffsetTop.current &&
        heightScrolled.current < headerHeight.current
      ) {
        heightScrolled.current = Math.min(
          heightScrolled.current + diff,
          headerHeight.current
        );
        setHeaderTop(heightScrolled.current - headerHeight.current);
      } else if (
        scrollTop < eventsHeaderOffsetTop.current &&
        heightScrolled.current > 0
      ) {
        heightScrolled.current = Math.max(heightScrolled.current + diff, 0);
        setHeaderTop(heightScrolled.current - headerHeight.current);
      }
      prevScrollTop.current = scrollTop;
    };
    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [headerRefs, isPageReady]);

  // set position on initial load
  useEffect(() => {
    const { scrollTop } = document.documentElement;
    if (scrollTop < eventsHeaderOffsetTop.current) {
      setHeaderTop(-headerHeight.current);
    } else {
      setHeaderTop(0);
    }
  }, []);

  const { isRegistered } = useSelector(({ summit }) => {
    return {
      isRegistered: summit.isRegistered
    };
  });

  const [showRegisterBtn, setShowRegisterBtn] = useState(false);
  const dates = useSelectorToStore(({ summit }) => {
    return summit.schedule.map((daySchedule) => daySchedule.date);
  });
  useEffect(() => {
    let showRegister = !isRegistered;
    if (showRegister) {
      const today = new Date();
      showRegister = !dates.every(
        (date) => dateHelper.compareDates(date, today) === 1
      );
    }
    setShowRegisterBtn(showRegister);
  }, [dates, isRegistered]);
  return (
    <StickyHeaderWrapper ref={headerRef} style={{ top: `${headerTop}px` }}>
      {isMobileDevice ? (
        <HeaderSectionsMobile headerRefs={headerRefs} />
      ) : (
        <Header>
          <StyledLogo isReload />
          <Grow />
          <HeaderSections
            headerRefs={headerRefs}
            showRegisterButton={showRegisterBtn}
          />
        </Header>
      )}
    </StickyHeaderWrapper>
  );
};

FixedHeader.propTypes = {
  headerRefs: PropTypes.objectOf(PropTypes.object).isRequired,
  isPageReady: PropTypes.bool.isRequired
};

export default FixedHeader;
